// src/views/AccommodationProvider/ProviderAddPropertyBedAllocation.js
import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Badge, Row, Col, Alert, Button, Input, FormGroup, Label, Table } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderAddPropertyBedAllocation = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // State
  const [addedCampuses, setAddedCampuses] = useState([]);
  const [bedAllocations, setBedAllocations] = useState({});
  const [totalBeds, setTotalBeds] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Get room details to calculate total beds
        const roomResponse = await axiosInstance.get('/api/property/room-details/store/');
        if (roomResponse.data.stored && roomResponse.data.data) {
          const { number_of_single_rooms, number_of_double_rooms } = roomResponse.data.data;
          const singles = parseInt(number_of_single_rooms) || 0;
          const doubles = parseInt(number_of_double_rooms) || 0;
          const total = singles + (doubles * 2);
          setTotalBeds(total);
        }

        // Get selected campuses
        const campusResponse = await axiosInstance.get('/api/property/campuses/store/');
        if (campusResponse.data.stored && campusResponse.data.data) {
          const campusIds = campusResponse.data.data.campus_ids;
          
          // Fetch campus details from institution_view
          const institutionResponse = await axiosInstance.get('/api/institution_view/');
          const allCampuses = [];
          
          institutionResponse.data.forEach(institution => {
            if (institution.campuses && Array.isArray(institution.campuses)) {
              institution.campuses.forEach(campus => {
                if (campusIds.includes(campus.id)) {
                  allCampuses.push({
                    id: campus.id,
                    name: campus.name,
                    institution_name: institution.name,
                    institution_abbrev: institution.abbrev
                  });
                }
              });
            }
          });
          
          setAddedCampuses(allCampuses);
          
          // Initialize bed allocations to 0
          const initialAllocations = {};
          allCampuses.forEach(campus => {
            initialAllocations[campus.id] = 0;
          });
          setBedAllocations(initialAllocations);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load data. Please try again.',
          visible: true
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Bed Allocation - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Bed Allocation - ${providerName || 'Your Organization'}`;
    } else {
      return 'Bed Allocation';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const handleBedAllocationChange = (campusId, value) => {
    const bedCount = parseInt(value) || 0;
    setBedAllocations({
      ...bedAllocations,
      [campusId]: bedCount
    });
  };

  const getTotalAllocated = () => {
    return Object.values(bedAllocations).reduce((sum, beds) => sum + beds, 0);
  };

  const getRemainingBeds = () => {
    return totalBeds - getTotalAllocated();
  };

  const handleBackToCampuses = () => {
    navigate('/provider-add-property-campuses');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setAlert({ type: '', message: '', visible: false });

    // Validate bed allocations
    const totalAllocated = getTotalAllocated();
    
    if (totalAllocated === 0) {
      setAlert({
        type: 'warning',
        message: 'Please allocate beds to at least one campus.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    if (totalAllocated > totalBeds) {
      setAlert({
        type: 'danger',
        message: `Total allocated beds (${totalAllocated}) exceeds available beds (${totalBeds}).`,
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    try {
      // Store bed allocations in session
      await axiosInstance.post('/api/property/bed-allocation/store/', {
        bed_allocations: bedAllocations
      });

      // Now create the complete property with all stored session data
      const createResponse = await axiosInstance.post('/api/property/create-complete/');

      setAlert({
        type: 'success',
        message: createResponse.data.message || 'Property created successfully!',
        visible: true
      });

      // Navigate to provider properties page after successful creation
      setTimeout(() => {
        navigate('/provider-properties');
      }, 2000);
    } catch (error) {
      console.error('Error saving bed allocation:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to save bed allocation. Please try again.',
        visible: true
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              <Badge color="secondary" className="me-2">
                Step 5 of 5
              </Badge>
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToCampuses}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Campuses
            </Button>
          </div>
        </div>
      </div>

      {/* Alert */}
      {alert.visible && (
        <Row className="mb-3">
          <Col md="12">
            <Alert color={alert.type} toggle={() => setAlert({...alert, visible: false})}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Bed Allocation Form */}
      <Row className="mb-4">
        <Col md="12">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-hospital-fill me-2 text-primary"></i>
                Allocate Beds to Campuses
              </CardTitle>

              <Alert color="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Allocate approximate bed counts for each campus. This is used for reporting purposes. 
                Actual bed availability will be managed dynamically during the booking process.
              </Alert>

              {/* Total Beds Summary */}
              <Row className="mb-4">
                <Col md="4">
                  <Card className="border-primary">
                    <CardBody className="text-center">
                      <h6 className="text-muted">Total Available Beds</h6>
                      <h2 className="text-primary mb-0">{totalBeds}</h2>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className="border-success">
                    <CardBody className="text-center">
                      <h6 className="text-muted">Allocated Beds</h6>
                      <h2 className="text-success mb-0">{getTotalAllocated()}</h2>
                    </CardBody>
                  </Card>
                </Col>
                <Col md="4">
                  <Card className={`border-${getRemainingBeds() < 0 ? 'danger' : 'secondary'}`}>
                    <CardBody className="text-center">
                      <h6 className="text-muted">Remaining Beds</h6>
                      <h2 className={`text-${getRemainingBeds() < 0 ? 'danger' : 'secondary'} mb-0`}>
                        {getRemainingBeds()}
                      </h2>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Loading State */}
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3 text-muted">Loading campus data...</p>
                </div>
              ) : (
                <>
                  {/* Campus Bed Allocation Table */}
                  {addedCampuses.length > 0 ? (
                    <Table responsive hover>
                      <thead className="table-light">
                        <tr>
                          <th>Campus</th>
                          <th>Institution</th>
                          <th style={{ width: '200px' }}>Allocated Beds</th>
                        </tr>
                      </thead>
                      <tbody>
                        {addedCampuses.map(campus => (
                          <tr key={campus.id}>
                            <td>
                              <strong>{campus.name}</strong>
                            </td>
                            <td>
                              <span className="text-muted">
                                {campus.institution_name} ({campus.institution_abbrev})
                              </span>
                            </td>
                            <td>
                              <FormGroup className="mb-0">
                                <Input
                                  type="number"
                                  min="0"
                                  max={totalBeds}
                                  value={bedAllocations[campus.id] || 0}
                                  onChange={(e) => handleBedAllocationChange(campus.id, e.target.value)}
                                  disabled={isSubmitting}
                                />
                              </FormGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <Alert color="warning">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      No campuses selected. Please go back and select at least one campus.
                    </Alert>
                  )}
                </>
              )}

              {/* Submit Button */}
              <Row className="mt-4">
                <Col md="12" className="text-center">
                  <Button 
                    color="primary" 
                    size="lg"
                    onClick={handleSubmit}
                    disabled={regStatus !== '2' || isSubmitting || isLoading || addedCampuses.length === 0 || getTotalAllocated() === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Creating Property...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Create Property
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row>
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-info-circle me-2"></i>
                About Bed Allocation
              </CardTitle>
              <p className="small text-muted mb-0">
                The bed counts you enter here are approximate allocations used for reporting and capacity planning. 
                You don't need to allocate all beds right now - unallocated beds can be assigned to campuses later. 
                During the actual booking process, bed availability will be managed dynamically based on real-time bookings.
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProviderAddPropertyBedAllocation;
